import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider, Elements } from '@stripe/react-stripe-js';
import { Loader2, X } from 'lucide-react';

// Initialize Stripe outside of the component to avoid recreation on renders
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY as string);

interface StripeCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientSecret: string | null;
  onPaymentSuccess: () => void;
}

const CheckoutForm = ({ onClose, onPaymentSuccess, clientSecret }: { onClose: () => void; onPaymentSuccess: () => void; clientSecret: string }) => {
  return (
    <div className="p-4">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          clientSecret,
          onComplete: onPaymentSuccess
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
      <button
        type="button"
        onClick={onClose}
        className="w-full text-gray-400 hover:text-white text-sm transition-colors mt-4"
      >
        Cancel
      </button>
    </div>
  );
};

export default function StripeCheckoutModal({ isOpen, onClose, clientSecret, onPaymentSuccess }: StripeCheckoutModalProps) {
  const [loading, setLoading] = useState(true);
  const [stripeInitError, setStripeInitError] = useState<string | null>(null);
  
  useEffect(() => {
    if (isOpen && clientSecret) {
      // Check if we have a valid Stripe publishable key
      if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY || 
          import.meta.env.VITE_STRIPE_PUBLIC_KEY.includes('YOUR_STRIPE_PUBLIC_KEY')) {
        setStripeInitError('Invalid Stripe publishable key. Please check your environment variables.');
        console.error('Invalid Stripe publishable key:', import.meta.env.VITE_STRIPE_PUBLIC_KEY);
      }
      
      // Short delay to ensure Stripe elements are loaded properly
      const timer = setTimeout(() => setLoading(false), 800);
      return () => clearTimeout(timer);
    }
  }, [isOpen, clientSecret]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="relative w-full max-w-lg mx-auto max-h-[90vh] bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-lg font-medium text-white">Complete Your Subscription</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="relative overflow-y-auto" style={{ maxHeight: '70vh' }}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
              <Loader2 className="w-8 h-8 text-[#c9fffc] animate-spin" />
            </div>
          )}
          
          {stripeInitError ? (
            <div className="p-6">
              <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
                <p className="font-medium mb-2">Stripe Configuration Error</p>
                <p>{stripeInitError}</p>
                <p className="mt-2 text-xs">Please check your .env file and ensure valid Stripe API keys are configured.</p>
              </div>
              <button
                onClick={onClose}
                className="w-full text-gray-400 hover:text-white text-sm transition-colors mt-4"
              >
                Close
              </button>
            </div>
          ) : clientSecret && (
            <CheckoutForm 
              onClose={onClose} 
              onPaymentSuccess={onPaymentSuccess} 
              clientSecret={clientSecret}
            />
          )}
        </div>
      </div>
    </div>
  );
}
